/** Scroll progress for one story section. */
export interface SectionProgress {
  /** 0 when the section's top reaches the bottom of the viewport, 1 when it is pinned at the top. */
  enter: number;
  /** 0 while pinned at the top, 1 when the section's bottom reaches the bottom of the viewport. */
  pin: number;
}

/** Section order: hero, the four acts, then whatever follows the story. */
export const STORY_SECTIONS = [
  'hero',
  'introduction',
  'signals',
  'connections',
  'intelligence',
  'outro',
] as const;

export type StoryTimeline = SectionProgress[];

export const createTimeline = (): StoryTimeline =>
  STORY_SECTIONS.map((_, index) => ({ enter: index === 0 ? 1 : 0, pin: 0 }));

/** Smoothed, per-frame values the 3D scene animates from. */
export interface SceneState {
  /** Act I: 0 bottle centred → 1 bottle aside (wide) or lifted (narrow). */
  shift: number;
  /** Act II: signal points and clusters coming into view. */
  signals: number;
  /** Act III: connections extending out and the camera pulling back. */
  connections: number;
  /** Act IV: pathways brightening and the hierarchy settling. */
  focus: number;
  /** After the story: the network fades and the scene recedes into a backdrop. */
  outro: number;
}

export const createSceneState = (): SceneState => ({
  shift: 0,
  signals: 0,
  connections: 0,
  focus: 0,
  outro: 0,
});

export const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const smoothstep = (value: number, edge0: number, edge1: number) => {
  const t = clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
};

export const lerp = (from: number, to: number, amount: number) => from + (to - from) * amount;
